import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { TextField } from "../../commons/form-fields/TextField.js";
import { useCreateProductListMutation } from "./CreateProductListModal.generated.js";

interface FormValues {
  title: string;
}

export const CreateProductListModal = ({
  refetchQueries,
  handleComplete,
  handleClose,
}: {
  refetchQueries?: string[];
  handleClose(): void;
  handleComplete?(productListId: string): void;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global", "ProductLists"]);

  const client = useApolloClient();
  const [create] = useCreateProductListMutation({ client });

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await create({
        variables: {
          input: {
            title: values.title,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      if (result.data?.createProductList.productList.id) {
        enqueueSnackbar(
          t("Product list added", {
            ns: "ProductLists",
          }),
          { variant: "success" }
        );
        handleComplete?.(result.data?.createProductList.productList.id);
        handleClose();
      }
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const initialValues: FormValues = {
    title: "",
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        title: Yup.string()
          .label(t("Title", { ns: "Global" }))
          .required(),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({ isSubmitting, dirty, isValid }) => (
        <Modal
          title={t("Add new product list", {
            ns: "ProductLists",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Create", { ns: "Global" }),
              buttonProps: {
                loading: isSubmitting,
                form: formId,
                type: "submit",
                disabled: !dirty || !isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1} direction="column">
              <TextField
                name="title"
                required
                label={t("Title", {
                  ns: "Global",
                })}
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
